import { useState, useEffect, useCallback } from 'react';
import { X, FileText, ExternalLink, RefreshCw, Upload } from 'lucide-react';
import type { PreviewPdfSettings, PreviewPdfRequest } from '../../types/api';

interface PreviewPdfModalProps {
  isOpen: boolean;
  language: string;
  isGenerating: boolean;
  previewUrl: string | null;
  settings: PreviewPdfSettings | null;
  error: string | null;
  onGenerate: (body: PreviewPdfRequest) => void;
  onUploadImage: (imageType: 'issuer' | 'badge', file: File) => Promise<string>;
  onClose: () => void;
}

export function PreviewPdfModal({
  isOpen,
  language,
  isGenerating,
  previewUrl,
  settings,
  error,
  onGenerate,
  onUploadImage,
  onClose,
}: PreviewPdfModalProps) {
  const [ownerName, setOwnerName] = useState('');
  const [title, setTitle] = useState('');
  const [issuerName, setIssuerName] = useState('');
  const [lang, setLang] = useState('');
  const [issuerImageBlobPath, setIssuerImageBlobPath] = useState('');
  const [badgeImageBlobPath, setBadgeImageBlobPath] = useState('');
  const [dirty, setDirty] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<'issuer' | 'badge' | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  // Sync form fields when settings arrive from API
  useEffect(() => {
    if (settings) {
      setOwnerName(settings.ownerName || '');
      setTitle(settings.title || '');
      setIssuerName(settings.issuerName || '');
      setLang(settings.language || '');
      setIssuerImageBlobPath(settings.issuerImageBlobPath || '');
      setBadgeImageBlobPath(settings.badgeImageBlobPath || '');
      setDirty(false);
    }
  }, [settings]);

  // Reset on open with language default
  useEffect(() => {
    if (isOpen && !settings) {
      setLang(language);
      setDirty(false);
    }
  }, [isOpen, language, settings]);

  // Fetch PDF as blob for inline display (SAS URLs force download otherwise)
  useEffect(() => {
    if (!previewUrl) {
      setPdfBlobUrl(null);
      return;
    }
    let revoked = false;
    fetch(previewUrl)
      .then((res) => res.blob())
      .then((blob) => {
        if (revoked) return;
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPdfBlobUrl(url);
      })
      .catch(() => setPdfBlobUrl(null));
    return () => {
      revoked = true;
      setPdfBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [previewUrl]);

  // Escape key to close
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const updateField = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setDirty(true);
  };

  const handleImageUpload = async (imageType: 'issuer' | 'badge') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setUploadingImage(imageType);
      try {
        const blobPath = await onUploadImage(imageType, file);
        if (imageType === 'issuer') {
          setIssuerImageBlobPath(blobPath);
        } else {
          setBadgeImageBlobPath(blobPath);
        }
        setDirty(true);
      } finally {
        setUploadingImage(null);
      }
    };
    input.click();
  };

  const handleGenerate = () => {
    onGenerate({
      ownerName: ownerName || undefined,
      title: title || undefined,
      issuerName: issuerName || undefined,
      language: lang || undefined,
      issuerImageBlobPath: issuerImageBlobPath || undefined,
      badgeImageBlobPath: badgeImageBlobPath || undefined,
    });
    setDirty(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full mx-4 transition-all duration-300 ${previewUrl && !isGenerating ? 'max-w-5xl' : 'max-w-lg'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Podglad PDF — {language.toUpperCase()}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body — side by side when PDF is showing */}
        <div className={`${previewUrl && !isGenerating ? 'flex' : ''}`}>
          {/* Form */}
          <div className={`p-5 space-y-3 ${previewUrl && !isGenerating ? 'w-80 shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto max-h-[70vh]' : ''}`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Imie i nazwisko
                </label>
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => updateField(setOwnerName)(e.target.value)}
                  placeholder="Anna Kowalska"
                  className="input-field w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Jezyk (format daty)
                </label>
                <input
                  type="text"
                  value={lang}
                  onChange={(e) => updateField(setLang)(e.target.value)}
                  placeholder={language}
                  className="input-field w-full text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Tytul
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => updateField(setTitle)(e.target.value)}
                placeholder="Tytul certyfikatu"
                className="input-field w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Nazwa wydawcy
              </label>
              <input
                type="text"
                value={issuerName}
                onChange={(e) => updateField(setIssuerName)(e.target.value)}
                placeholder="Nazwa organizacji"
                className="input-field w-full text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Logo wydawcy
                </label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={issuerImageBlobPath}
                    onChange={(e) => updateField(setIssuerImageBlobPath)(e.target.value)}
                    placeholder="container/logo.png"
                    className="input-field w-full text-sm min-w-0"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('issuer')}
                    disabled={uploadingImage === 'issuer'}
                    className="btn-secondary text-xs px-2 shrink-0"
                    title="Upload logo"
                  >
                    {uploadingImage === 'issuer' ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Badge
                </label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={badgeImageBlobPath}
                    onChange={(e) => updateField(setBadgeImageBlobPath)(e.target.value)}
                    placeholder="container/badge.png"
                    className="input-field w-full text-sm min-w-0"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('badge')}
                    disabled={uploadingImage === 'badge'}
                    className="btn-secondary text-xs px-2 shrink-0"
                    title="Upload badge"
                  >
                    {uploadingImage === 'badge' ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                {error}
              </div>
            )}

            {dirty && previewUrl && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Zmieniono ustawienia — kliknij &quot;Generuj ponownie&quot;
              </p>
            )}
          </div>

          {/* PDF Preview */}
          {previewUrl && !isGenerating && (
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50">
                <span className="text-xs text-gray-500 dark:text-gray-400">Podglad wygenerowanego PDF</span>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Pobierz PDF
                </a>
              </div>
              {pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  className="flex-1 w-full min-h-[70vh] border-0"
                  title="PDF Preview"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center min-h-[70vh]">
                  <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">
            Zamknij
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Generowanie...
              </>
            ) : (
              <>
                <FileText className="h-3.5 w-3.5" />
                {previewUrl ? 'Generuj ponownie' : 'Generuj PDF'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
